import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw, Download } from "lucide-react";
import { UsersDataTable } from "../UsersDataTable";
import type { AdminUser } from "../UsersDataTable";
import { toast } from "sonner";

// Platform User interface (for managing users)
interface PlatformUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  userType: "individual" | "company";
  companyName?: string;
  siret?: string;
  subscriptionPlan: string;
  subscriptionStatus: "active" | "inactive" | "pending";
  subscriptionEndDate?: string;
  isEmailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  customerId?: string;
  subscriptionId?: string;
}

interface UsersTabProps {
  users: PlatformUser[];
  usersLoading: boolean;
  usersError: string | null;
  onViewUser: (user: AdminUser) => void;
  onRefreshUsers: () => void;
}

export const UsersTab: React.FC<UsersTabProps> = ({
  users,
  usersLoading,
  usersError,
  onViewUser,
  onRefreshUsers,
}) => {
  // Convert PlatformUser to AdminUser for the table
  const convertedUsers: AdminUser[] = users.map((user) => ({
    ...user,
  }));

  const handleRefreshUsers = () => {
    onRefreshUsers();
    toast.success("Actualisation des données utilisateurs en cours...");
  };

  // Convert users data to CSV format
  const convertToCSV = (users: PlatformUser[]): string => {
    if (users.length === 0) {
      return "Aucune donnée à exporter";
    }

    // Define CSV headers
    const headers = [
      "ID",
      "Email",
      "Prénom",
      "Nom",
      "Téléphone",
      "Adresse",
      "Ville",
      "Code Postal",
      "Type d'Utilisateur",
      "Nom de l'Entreprise",
      "SIRET",
      "Plan d'Abonnement",
      "Statut d'Abonnement",
      "Date de Fin d'Abonnement",
      "Email Vérifié",
      "ID Client Stripe",
      "ID Abonnement Stripe",
      "Date de Création",
      "Date de Mise à Jour",
    ];

    // Convert data to CSV rows
    const csvData = users.map((user) => [
      user.id,
      user.email,
      user.firstName || "",
      user.lastName || "",
      user.phone || "",
      user.address || "",
      user.city || "",
      user.postalCode || "",
      user.userType === "company" ? "Entreprise" : "Particulier",
      user.companyName || "",
      user.siret || "",
      user.subscriptionPlan || "",
      user.subscriptionStatus,
      user.subscriptionEndDate
        ? new Date(user.subscriptionEndDate).toLocaleDateString("fr-FR")
        : "",
      user.isEmailVerified ? "Oui" : "Non",
      user.customerId || "",
      user.subscriptionId || "",
      new Date(user.createdAt).toLocaleDateString("fr-FR"),
      new Date(user.updatedAt).toLocaleDateString("fr-FR"),
    ]);

    // Escape and quote CSV fields that contain commas, quotes, or newlines
    const escapeCSVField = (field: string): string => {
      if (field.includes(",") || field.includes('"') || field.includes("\n")) {
        return `"${field.replace(/"/g, '""')}"`;
      }
      return field;
    };

    // Combine headers and data
    const allRows = [headers, ...csvData];

    // Convert to CSV string
    return allRows.map((row) => row.map(escapeCSVField).join(",")).join("\n");
  };

  // Trigger CSV download
  const downloadCSV = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  // Handle users export
  const handleExportUsers = () => {
    try {
      if (users.length === 0) {
        toast.warning("Aucune donnée utilisateur à exporter");
        return;
      }

      const csvContent = convertToCSV(users);
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      const filename = `utilisateurs_export_${timestamp}.csv`;

      downloadCSV(csvContent, filename);

      toast.success(
        `Export réussi ! ${users.length} utilisateurs exportés dans ${filename}`,
      );
    } catch (error) {
      console.error("Erreur lors de l'export CSV:", error);
      toast.error("Erreur lors de l'export des données utilisateurs");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Gestion des Utilisateurs
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gérez les comptes utilisateurs et leurs permissions
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefreshUsers}
            disabled={usersLoading}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${usersLoading ? "animate-spin" : ""}`}
            />
            {usersLoading ? "Actualisation..." : "Actualiser"}
          </Button>
          <Button
            variant="outline"
            onClick={handleExportUsers}
            disabled={usersLoading || users.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Exporter les Données
          </Button>
        </div>
      </div>

      {usersError ? (
        <Card className="border-red-200 dark:border-red-800">
          <CardContent className="p-6 text-center">
            <p className="text-red-600 dark:text-red-400">
              Erreur: {usersError}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-6">
            <UsersDataTable
              data={convertedUsers}
              loading={usersLoading}
              onViewUser={onViewUser}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};
